import * as React from 'react';
import { Team, TradeProposalTeam, TradeProposalPlayer, Player, TradeProposalPick, DraftPick } from '../../../sdk/model';
import {TableContainer, Paper, Table, TableHead, TableRow, TableBody, Typography, Box, Card, CardContent, Chip, useMediaQuery} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { displayDraftPick, displayDraftPickDetails } from '../../util/DraftPickDisplay';
import PlayerCardLink from '../../cards/playerCardLink';
import DraftPickCardLink from '../../cards/draftPickCardLink';
import TeamDisplay from '../team/TeamDisplay';
import { toSalary } from '../../util/SalaryDisplay';


interface tradeAtAGlanceProps {
  leagueId: string,
  tradeStatus: string,
  teams: Array<TradeProposalTeam>,
  picks: Array<TradeProposalPick>,
  players: Array<TradeProposalPlayer>,
  playerMap: Map<string | undefined, Player | undefined>,
  teamMap: Map<string | undefined, Team | undefined>,
  pickMap: Map<string | undefined, DraftPick | undefined> | undefined
}

function TradeAtAGlance(props: tradeAtAGlanceProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  function getPlayerDetails(tradedPlayer : TradeProposalPlayer, isReceiving: boolean) {
    const player = props.playerMap.get(tradedPlayer.playerId);
    const totalTradingTeamDeadCapPayout = Object.values(tradedPlayer.tradingTeamDeadCapPayouts!).reduce((sum, payout) => sum + payout, 0);
    return <React.Fragment key={player?.id}>
      <p><PlayerCardLink player={player} leagueId={props.leagueId} /></p>
      {!isReceiving && totalTradingTeamDeadCapPayout !== 0 && <Typography sx={{color : '#FF0000'}}>Covering {toSalary(totalTradingTeamDeadCapPayout)} in Dead Cap</Typography>}
      {isReceiving && tradedPlayer.removeFromIr && <p>(returned from IR)</p>}
    </React.Fragment>;
  }
  function getPickDetails(tradedPick : TradeProposalPick) {
    if (tradedPick.originalTeamId) {
      const originalTeam = props.teamMap.get(tradedPick.originalTeamId);
      return <p key={tradedPick.draftPickId}><DraftPickCardLink pick={undefined} pickId={tradedPick.draftPickId} leagueId={props.leagueId}>{displayDraftPickDetails(tradedPick.seasonYear, tradedPick.roundNumber, tradedPick.pickNumber, originalTeam)}</DraftPickCardLink></p>;
    } else if (props.pickMap) {
      const pick = props.pickMap.get(tradedPick?.draftPickId);
      const originalTeam = props.teamMap.get(pick?.originalTeamId);
      return <p key={pick?.id}><DraftPickCardLink pick={pick} pickId={pick?.id} leagueId={props.leagueId}>{displayDraftPick(pick, originalTeam)}</DraftPickCardLink></p>;
    }
    return "Error getting pick details"
  }
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));
  // Mobile layout - Cards for each team
  if (isMobile) {
    return (
      <Box>
        {props.teams.filter(team => team).map(tradeTeam => {
          const team = props.teamMap.get(tradeTeam.teamId)!;
          const tradedPlayers = props.players.filter(player => player.fromTeamId === team.id && player.action === 'TRADE');
          const droppedPlayers = props.players.filter(player => player.fromTeamId === team.id && player.action === 'DROP');
          const tradedPicks = props.picks.filter(pick => pick.fromTeamId === team.id);
          const receivedPlayers = props.players.filter(player => player.toTeamId === team.id);
          const receivedPicks = props.picks.filter(pick => pick.toTeamId === team.id);
          
          const hasTraded = tradedPlayers.length > 0 || droppedPlayers.length > 0 || tradedPicks.length > 0;
          const hasReceived = receivedPlayers.length > 0 || receivedPicks.length > 0;
          
          return (
            <Card key={team.id} sx={{ mb: 2, borderLeft: 4, borderLeftColor: theme.palette.primary.main }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h6" component="div">
                    <TeamDisplay leagueId={props.leagueId} team={team} emphasized={false} teamId={tradeTeam.teamId} />
                  </Typography>
                  {props.tradeStatus !== 'DRAFT' && (
                    <Chip 
                      label={tradeTeam.status} 
                      size="small" 
                      color={tradeTeam.status === 'ACCEPTED' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  )}
                </Box>
                
                {hasTraded && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                      Trading Away:
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      {tradedPlayers.map(player => getPlayerDetails(player, false))}
                      {droppedPlayers.map(player => getPlayerDetails(player, false))}
                      {tradedPicks.map(pick => getPickDetails(pick))}
                    </Box>
                  </Box>
                )}
                
                {hasReceived && (
                  <Box>
                    <Typography variant="subtitle2" color="success.main" sx={{ mb: 1 }}>
                      Receiving:
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      {receivedPlayers.map(player => getPlayerDetails(player, true))}
                      {receivedPicks.map(pick => getPickDetails(pick))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  }
  
  // Desktop layout - Enhanced table with better styling
  return <TableContainer component={Paper} sx={{ borderLeft: 4, borderLeftColor: theme.palette.primary.main }}>
    <Table sx={{ minWidth: 650 }} aria-label="simple table">
      <TableHead sx={{fontSize : 20}}>
        <TableRow>
          <StyledTableCell>Team</StyledTableCell>
          {props.tradeStatus === 'DRAFT' ? null : <StyledTableCell>Status</StyledTableCell>}
          <StyledTableCell sx={{ backgroundColor: theme.palette.error.light + '20', color: theme.palette.error.dark }}>
            Trading Away
          </StyledTableCell>
          <StyledTableCell sx={{ backgroundColor: theme.palette.success.light + '20', color: theme.palette.success.dark }}>
            Receiving
          </StyledTableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {props.teams.filter(team => team).map(tradeTeam => {
          const team = props.teamMap.get(tradeTeam.teamId)!;
          const tradedPlayers = props.players.filter(player => player.fromTeamId === team.id && player.action === 'TRADE');
          const droppedPlayers =props.players.filter(player => player.fromTeamId === team.id && player.action === 'DROP')
          const tradedPicks = props.picks.filter(pick => pick.fromTeamId === tradeTeam.teamId);
          const receivedPlayers = props.players.filter(player => player.toTeamId === team.id);
          const receivedPicks = props.picks.filter(pick => pick.toTeamId === tradeTeam.teamId);
          
          const getStatusColor = () => {
            switch(tradeTeam.status) {
              case 'ACCEPTED': return 'success';
              case 'PENDING': return 'warning';
              case 'REJECTED': return 'error';
              default: return 'default';
            }
          };
          
          return <TableRow key={tradeTeam.teamId} sx={{ '&:hover': { backgroundColor: theme.palette.action.hover } }}>
            <StyledTableCell>
              <Typography variant="subtitle2" fontWeight="bold">
                <TeamDisplay leagueId={props.leagueId} team={team} emphasized={false} teamId={tradeTeam.teamId} />
              </Typography>
            </StyledTableCell>
            {props.tradeStatus === 'DRAFT' ? null : (
              <StyledTableCell>
                <Chip 
                  label={tradeTeam.status} 
                  color={getStatusColor()} 
                  size="small"
                  variant={tradeTeam.status === 'ACCEPTED' ? 'filled' : 'outlined'}
                />
              </StyledTableCell>
            )}
            <StyledTableCell sx={{ backgroundColor: theme.palette.error.light + '08' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {tradedPlayers.map(player => getPlayerDetails(player, false))}
                {droppedPlayers.map(player => getPlayerDetails(player, false))}
                {tradedPicks.map(pick => getPickDetails(pick))}
                {(tradedPlayers.length === 0 && droppedPlayers.length === 0 && tradedPicks.length === 0) && (
                  <Chip label="Nothing" variant="outlined" size="small" />
                )}
              </Box>
            </StyledTableCell>
            <StyledTableCell sx={{ backgroundColor: theme.palette.success.light + '08' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {receivedPlayers.map(player => getPlayerDetails(player, true))}
                {receivedPicks.map(pick => getPickDetails(pick))}
                {(receivedPlayers.length === 0 && receivedPicks.length === 0) && (
                  <Chip label="Nothing" variant="outlined" size="small" />
                )}
              </Box>
            </StyledTableCell>
          </TableRow>
        })}
      </TableBody>
    </Table>
  </TableContainer>
}

export default TradeAtAGlance;
